import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import BakerLocationSection from "@/components/BakerLocationSection";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, MapPin, MessageSquare, User as UserIcon, KeyRound, Loader2, ShieldAlert, ExternalLink, Sparkles } from "lucide-react";

const TABS = [
  { id: "profile", label: "Profile", icon: UserIcon },
  { id: "finder", label: "Finder Info", icon: MapPin },
  { id: "dms", label: "DMs", icon: MessageSquare },
  { id: "impersonate", label: "View as user", icon: KeyRound },
];

export default function AdminUserDrilldown() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("profile");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/admin/users/${userId}/location`)
      .then((r) => setInfo(r.data))
      .catch((e) => { setErr(formatApiError(e.response?.data?.detail) || "Couldn't load user"); setInfo({}); });
  }, [userId]);

  if (info === null) {
    return (
      <>
        <Header />
        <main className="max-w-5xl mx-auto p-6 grid place-items-center min-h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-[#857163]" />
        </main>
      </>
    );
  }

  if (err || !info.user) {
    return (
      <>
        <Header />
        <main className="max-w-5xl mx-auto p-6">
          <div className="cp-card p-8 text-center">
            <ShieldAlert className="w-10 h-10 text-[#C2185B] mx-auto mb-3" />
            <div className="font-semibold text-[#3B2C24]">Couldn't load this user</div>
            <p className="text-sm text-[#857163] mt-2">{err}</p>
            <Link to="/admin" className="mt-6 inline-block cp-btn-primary text-sm">Back to Admin</Link>
          </div>
        </main>
      </>
    );
  }

  const u = info.user;

  return (
    <>
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-6" data-testid="admin-user-drilldown">
        <button
          onClick={() => nav("/admin")}
          className="inline-flex items-center gap-1.5 text-sm text-[#857163] hover:text-[#3B2C24] mb-4"
          data-testid="drilldown-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>

        {/* Header card */}
        <div className="cp-card p-5 sm:p-6 mb-5 flex items-start gap-4 flex-wrap">
          <Avatar className="w-16 h-16 border border-[#EBDCD1]">
            <AvatarImage src={fileUrl(u.avatar_url)} />
            <AvatarFallback className="bg-[#FFD07F] text-[#3B2C24] font-semibold text-lg">
              {u.username?.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#3B2C24]">@{u.username}</h1>
              {u.is_tester && <span className="cp-tag" style={{ background: "rgba(47,128,237,0.15)", color: "#1D5FBF" }}>Tester</span>}
              {u.is_premium && <span className="cp-tag" style={{ background: "rgba(244,168,59,0.20)", color: "#B26A00" }}>Premium</span>}
              {u.role === "admin" && <span className="cp-tag" style={{ background: "rgba(220,38,38,0.15)", color: "#B91C1C" }}>Admin</span>}
              {u.is_banned && <span className="cp-tag" style={{ background: "rgba(220,38,38,0.15)", color: "#B91C1C" }}>Banned</span>}
            </div>
            <div className="text-sm text-[#857163] mt-1">{u.email}</div>
            <Link to={`/u/${u.username}`} target="_blank" className="mt-2 inline-flex items-center gap-1 text-xs text-[#2F80ED] hover:underline" data-testid="drilldown-open-profile">
              Open public profile <ExternalLink className="w-3 h-3" />
            </Link>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  const target = !u.is_premium;
                  const label = target ? "Grant free premium to" : "Remove premium from";
                  if (!window.confirm(`${label} @${u.username}?`)) return;
                  try {
                    await api.patch(`/admin/users/${userId}/premium`, { is_premium: target });
                    toast.success(target ? "Free premium granted 🌟" : "Premium removed");
                    setInfo({ ...info, user: { ...u, is_premium: target } });
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition ${u.is_premium ? "bg-white border-[#FFD07F] text-[#B26A00] hover:bg-[#FFF9EF]" : "bg-[#FFD07F] border-[#F4A83B] text-[#3B2C24] hover:bg-[#FFC85C]"}`}
                data-testid="grant-free-premium-btn"
              >
                <Sparkles className="w-3.5 h-3.5" /> {u.is_premium ? "Remove premium" : "Grant free premium"}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition ${
                  tab === t.id
                    ? "bg-[#3B2C24] text-white"
                    : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
                }`}
                data-testid={`drilldown-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "profile" && <ProfileTab user={u} />}
        {tab === "finder" && (
          <BakerLocationSection
            mode="independent"
            username={u.username}
            adminTargetUserId={userId}
          />
        )}
        {tab === "dms" && <DMsTab userId={userId} username={u.username} />}
        {tab === "impersonate" && <ImpersonateTab userId={userId} user={u} />}
      </main>
    </>
  );
}

function ProfileTab({ user }) {
  const rows = [
    ["Email", user.email],
    ["Role", user.role],
    ["Subscription", user.subscription_status],
    ["Trial ends", user.trial_ends_at || "—"],
    ["City", user.city || "—"],
    ["State", user.state || "—"],
    ["Country", user.country || "—"],
    ["Seller?", user.is_seller ? "Yes" : "No"],
    ["Joined", user.created_at?.slice(0, 10) || "—"],
  ];
  return (
    <div className="cp-card p-6" data-testid="drilldown-profile-tab">
      <h2 className="font-heading text-xl text-[#3B2C24] mb-4">Account details</h2>
      <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 py-1 border-b border-[#EBDCD1]/60">
            <dt className="text-[#857163]">{k}</dt>
            <dd className="text-[#3B2C24] font-medium text-right break-all">{String(v ?? "—")}</dd>
          </div>
        ))}
      </dl>
      {user.bio && (
        <div className="mt-5">
          <div className="text-xs uppercase tracking-widest text-[#857163] mb-1">Bio</div>
          <p className="text-sm text-[#3B2C24] whitespace-pre-wrap">{user.bio}</p>
        </div>
      )}
    </div>
  );
}

function DMsTab({ userId, username }) {
  const [convos, setConvos] = useState(null);
  const [openWith, setOpenWith] = useState(null);
  const [messages, setMessages] = useState(null);

  useEffect(() => {
    api.get(`/admin/users/${userId}/conversations`)
      .then((r) => setConvos(r.data))
      .catch(() => setConvos([]));
  }, [userId]);

  const openConvo = (partner) => {
    setOpenWith(partner);
    setMessages(null);
    api.get(`/admin/users/${userId}/messages/with/${partner.id}`)
      .then((r) => setMessages(r.data))
      .catch(() => setMessages([]));
  };

  if (convos === null) {
    return <div className="cp-card p-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  if (!convos.length) {
    return <div className="cp-card p-6 text-sm text-[#857163]" data-testid="drilldown-dms-empty">@{username} has no direct messages yet.</div>;
  }

  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-4" data-testid="drilldown-dms-tab">
      <div className="cp-card p-2 overflow-hidden">
        <div className="px-3 py-2 text-xs uppercase tracking-widest text-[#857163]">Conversations</div>
        {convos.map((c) => (
          <button
            key={c.partner.id}
            onClick={() => openConvo(c.partner)}
            className={`w-full text-left px-3 py-2 rounded-2xl mb-1 transition ${openWith?.id === c.partner.id ? "bg-[#F9EFE6]" : "hover:bg-[#F9EFE6]"}`}
            data-testid={`drilldown-convo-${c.partner.username}`}
          >
            <div className="flex items-center gap-2">
              <Avatar className="w-8 h-8">
                <AvatarImage src={fileUrl(c.partner.avatar_url)} />
                <AvatarFallback className="text-[10px]">{c.partner.username?.slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[#3B2C24] truncate">@{c.partner.username}</div>
                <div className="text-xs text-[#857163] truncate">{c.last_message.body}</div>
              </div>
            </div>
          </button>
        ))}
      </div>
      <div className="cp-card p-4 min-h-[400px]">
        {!openWith ? (
          <div className="text-sm text-[#857163] p-4">Pick a conversation on the left to read the full transcript.</div>
        ) : messages === null ? (
          <div className="grid place-items-center h-40"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div>
            <div className="text-sm font-semibold text-[#3B2C24] mb-3">Transcript with @{openWith.username}</div>
            <div className="space-y-2 max-h-[480px] overflow-y-auto">
              {messages.map((m) => {
                const fromTarget = m.sender_id === userId;
                return (
                  <div key={m.id} className={`text-sm p-2.5 rounded-2xl max-w-[85%] ${fromTarget ? "bg-[#FF7E67]/15 text-[#3B2C24]" : "bg-[#F9EFE6] text-[#3B2C24] ml-auto"}`}>
                    <div className="text-[10px] text-[#857163] mb-0.5">
                      {fromTarget ? `@${username}` : `@${openWith.username}`} · {m.created_at?.slice(0, 16).replace("T", " ")}
                    </div>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ImpersonateTab({ userId, user }) {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    if (!window.confirm(`Impersonate @${user.username}?\n\nYou'll see the app exactly as they see it for 15 minutes. This is audit-logged. Do NOT change data unless you're helping them fix something they've asked for.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/impersonate`);
      toast.success(`Now viewing as @${user.username} — opening home tab`);
      // Reload so AuthContext picks up the new access_token cookie
      window.location.href = "/";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy(false);
  };
  return (
    <div className="cp-card p-6" data-testid="drilldown-impersonate-tab">
      <h2 className="font-heading text-xl text-[#3B2C24] mb-2 flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-[#F4A83B]" /> View the app as @{user.username}
      </h2>
      <p className="text-sm text-[#857163] mb-5 leading-relaxed">
        This grants your browser a 15-minute session that authenticates as the target user. You'll see their premium tabs, DMs, and any personalization exactly as they do — useful for debugging "it doesn't work for me" reports.
      </p>
      <ul className="text-sm text-[#3B2C24] space-y-2 mb-5 list-disc pl-5">
        <li>Session expires automatically in 15 minutes</li>
        <li>Every impersonation is written to the server audit log</li>
        <li>To return to your own account, simply log out and back in</li>
        <li>Cannot be used against another admin</li>
      </ul>
      <button
        onClick={start}
        disabled={busy}
        className="cp-btn-primary inline-flex items-center gap-2 disabled:opacity-60"
        data-testid="drilldown-impersonate-btn"
      >
        <KeyRound className="w-4 h-4" />
        {busy ? "Starting…" : `Start viewing as @${user.username}`}
      </button>
    </div>
  );
}
